/**
 * The second-tap wording of the console's two-step deletes (console spec §5).
 * The app has no dialog primitive, so the button becomes its own confirmation
 * — and a confirmation that carries a count is what makes an organizer read
 * it. Pure, so the counts can be tested without a click.
 */

const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** Deleting a pool unassigns its entries: "Confirm — unpools 3 teams". */
export function poolDeleteConfirm(entries: number): string {
  return entries > 0 ? `Confirm — unpools ${count(entries, "team")}` : "Confirm delete";
}

/**
 * Removing an entry. A registration-born entry goes back to the queue; a team
 * on unsettled games stays named on them until someone changes the slots.
 */
export function entryRemoveConfirm(input: {
  team: string;
  fromRegistration: boolean;
  scheduledGames: number;
}): string {
  const parts = [`Remove ${input.team}`];
  if (input.fromRegistration) parts.push(`returns ${input.team} to the registration queue`);
  if (input.scheduledGames > 0) {
    parts.push(`it stays on ${count(input.scheduledGames, "scheduled game")} until you change them`);
  }
  return parts.join(" — ");
}

/** Deleting a bracket: "Confirm — removes 7 games, 3 with results". */
export function bracketDeleteConfirm(games: number, withResults: number): string {
  const base = `Confirm — removes ${count(games, "game")}`;
  return withResults > 0 ? `${base}, ${withResults} with results` : base;
}

/**
 * Changing the timezone under scheduled games: what they would read as if the
 * stored instants are kept. "12 scheduled games will read 1 hour earlier".
 * Null when nothing would change (no games, or zones on the same clock).
 */
export function timezoneShiftConfirm(scheduledGames: number, shiftMinutes: number): string | null {
  if (scheduledGames === 0 || shiftMinutes === 0) return null;
  const abs = Math.abs(shiftMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  const span = [hours > 0 ? count(hours, "hour") : "", minutes > 0 ? count(minutes, "minute") : ""]
    .filter(Boolean)
    .join(" ");
  return `${count(scheduledGames, "scheduled game")} will read ${span} ${shiftMinutes > 0 ? "later" : "earlier"}`;
}
